import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  where,
  arrayUnion,
  arrayRemove,
  deleteField,
  setDoc,
  startAfter,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import db from "@/firebase/init.js";
import { HTTP } from "./http";

/**
 * Hybrid ChatService class for managing chat operations
 * 🔥 Firebase: Real-time messaging operations
 * 🌐 HTTP API: Team chat group management
 */
class ChatService {
  constructor() {
    this.listeners = new Map(); // Track active Firebase listeners for cleanup
    this.messagesCache = new Map(); // Cache messages for offline support
  }

  // ===========================================
  // 🔥 FIREBASE OPERATIONS (CHAT MESSAGES)
  // ===========================================

  /**
   * Get real-time chat messages using Firebase onSnapshot with smart pagination
   * @param {string|number} chatId - The chat/group ID
   * @param {string|number} clubId - The club ID for filtering
   * @param {Function} callback - Callback function to handle message updates
   * @param {number} limit - Number of messages to load (default: 20)
   * @returns {Function} Unsubscribe function
   */
  /**
   * Watch unread counts AND last-message previews for a set of chat
   * groups from ONE listener per group. (Rules require a chatId filter
   * on every query — membership is checked per chat — so a single
   * club-wide listener isn't permitted; this is the cheapest legal
   * shape.) Calls back with ({ [groupId]: count }, { [groupId]:
   * {lastMessage, lastMessageSender, lastMessageTime} }) on every
   * change. Returns a function that tears down all listeners.
   */
  watchUnreadCounts(chatGroups, clubId, userId, callback, sampleSize = 30) {
    const counts = {};
    const lastMessages = {};
    const unsubscribers = [];

    (chatGroups || []).forEach((group) => {
      if (!group || group.id === undefined) return;

      const q = query(
        collection(db, "team_chat_messages"),
        where("chatId", "==", String(group.id)),
        where("clubId", "==", String(clubId)),
        orderBy("createdDate", "desc"),
        limit(sampleSize),
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          let unread = 0;
          let newest = null;
          snapshot.forEach((doc) => {
            const data = doc.data();
            if (!newest) newest = data; // query is newest-first
            const readBy = Array.isArray(data.readById) ? data.readById : [];
            if (
              String(data.userId) !== String(userId) &&
              !readBy.map(String).includes(String(userId))
            ) {
              unread++;
            }
          });
          counts[group.id] = unread;
          if (newest) {
            lastMessages[String(group.id)] = {
              lastMessage:
                newest.messageType === "poll"
                  ? `📊 Poll: ${newest.poll?.question || newest.messageContent || ""}`
                  : newest.messageContent || (newest.media ? "[Media]" : ""),
              lastMessageSender: newest.name,
              lastMessageTime: newest.createdDate,
            };
          }
          callback({ ...counts }, { ...lastMessages });
        },
        (error) => {
          // A group we can't read (not a member) simply reports 0.
          console.error(
            `ChatService: unread listener failed for chat ${group.id}:`,
            error.message,
          );
          counts[group.id] = 0;
          callback({ ...counts }, { ...lastMessages });
        },
      );
      unsubscribers.push(unsubscribe);
    });

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }

  /**
   * Toggle an emoji reaction on a message. `hasReacted` is the user's
   * current state (caller knows it from the message in memory).
   * Rules allow members to touch only the `reactions` field.
   */
  async toggleReaction(messageId, emoji, userId, hasReacted) {
    const messageRef = doc(db, "team_chat_messages", String(messageId));
    await updateDoc(messageRef, {
      [`reactions.${emoji}`]: hasReacted
        ? arrayRemove(String(userId))
        : arrayUnion(String(userId)),
    });
  }

  /**
   * Create a poll message. A poll is a normal team_chat_messages doc with
   * messageType "poll" and a `poll` payload; votes live in poll.votes
   * ({userId: [optionIndex,...]}). It renders + updates live via the same
   * onSnapshot listener as every other message — no new collection.
   */
  async sendPoll(chatId, pollData) {
    const question = (pollData.question || "").trim();
    const options = (pollData.options || [])
      .map((o) => String(o || "").trim())
      .filter((o) => o.length > 0);
    if (!chatId || !question || options.length < 2) {
      throw new Error(
        "ChatService: a poll needs a question and at least 2 options",
      );
    }
    const message = {
      chatId: String(chatId),
      messageContent: question, // drives notification/push previews
      messageType: "poll",
      name: pollData.user_name || "Unknown User",
      userId: String(pollData.user_id),
      userImageURL: pollData.user_profile_image || "",
      media: "",
      createdDate: new Date().toISOString(),
      clubId: String(pollData.clubId),
      readById: [String(pollData.user_id)],
      poll: {
        question,
        options,
        allowMultiple: !!pollData.allowMultiple,
        votes: {}, // userId -> [optionIndex, ...]
      },
    };
    await addDoc(collection(db, "team_chat_messages"), message);
    return true;
  }

  /**
   * Cast/change a vote. Mirrors toggleReaction: the update touches only the
   * poll votes, so the firestore `onlyVoting` rule lets any chat member vote
   * without being able to edit the poll itself. `myVotes` is the caller's
   * current selections for this poll (read from the live doc).
   *   - single-select : replace with [optionIndex], or clear if re-tapped
   *   - multi-select  : toggle optionIndex within the user's array
   */
  async voteOnPoll(messageId, optionIndex, userId, allowMultiple, myVotes = []) {
    const messageRef = doc(db, "team_chat_messages", String(messageId));
    const key = `poll.votes.${String(userId)}`;
    const already = Array.isArray(myVotes) && myVotes.includes(optionIndex);
    if (allowMultiple) {
      await updateDoc(messageRef, {
        [key]: already ? arrayRemove(optionIndex) : arrayUnion(optionIndex),
      });
    } else {
      await updateDoc(messageRef, { [key]: already ? [] : [optionIndex] });
    }
  }

  /**
   * Typing indicator: merge/remove the user's entry in the chat's
   * typing doc (`team_chat_typing/{clubId}_{chatId}`). Throttling is
   * the caller's job; this is one write per call.
   */
  async setTyping(chatId, clubId, userId, userName, isTyping) {
    const ref = doc(db, "team_chat_typing", `${clubId}_${chatId}`);
    if (isTyping) {
      await setDoc(
        ref,
        {
          clubId: String(clubId),
          chatId: String(chatId),
          typers: {
            [String(userId)]: {
              name: userName || "Someone",
              at: new Date().toISOString(),
            },
          },
        },
        { merge: true },
      );
    } else {
      await setDoc(
        ref,
        {
          clubId: String(clubId),
          chatId: String(chatId),
          typers: { [String(userId)]: deleteField() },
        },
        { merge: true },
      );
    }
  }

  /**
   * Watch who's typing in a chat. Calls back with an array of names
   * (excluding the current user), freshness-filtered to the last 6s.
   * Returns an unsubscribe function.
   */
  watchTyping(chatId, clubId, currentUserId, callback) {
    const ref = doc(db, "team_chat_typing", `${clubId}_${chatId}`);
    let latest = {};
    const emit = () => {
      const now = Date.now();
      const names = Object.entries(latest)
        .filter(
          ([uid, t]) =>
            String(uid) !== String(currentUserId) &&
            t &&
            t.at &&
            now - new Date(t.at).getTime() < 6000,
        )
        .map(([, t]) => t.name || "Someone");
      callback(names);
    };
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        latest = (snap.exists() && snap.data().typers) || {};
        emit();
      },
      () => {
        latest = {};
        emit();
      },
    );
    // Periodic re-filter so an entry orphaned by a killed client
    // still expires from the banner without a new snapshot.
    const tick = setInterval(emit, 3000);
    return () => {
      clearInterval(tick);
      unsubscribe();
    };
  }

  getChatMessages(chatId, clubId, callback, limitCount = 20) {
    if (!chatId || !clubId || typeof callback !== "function") {
      console.error("ChatService: chatId, clubId and callback are required", {
        chatId,
        clubId,
        callback,
      });
      throw new Error("ChatService: chatId, clubId and callback are required");
    }

    console.log(
      `ChatService: Setting up Firebase listener for chatId: ${chatId}, clubId: ${clubId}, limit: ${limitCount}`,
    );

    try {
      const messagesRef = collection(db, "team_chat_messages");
      const q = query(
        messagesRef,
        where("chatId", "==", String(chatId)),
        where("clubId", "==", String(clubId)),
        orderBy("createdDate", "desc"), // Changed to desc for latest messages first
        limit(limitCount), // Use configurable limit
      );

      const unsubscribe = onSnapshot(
        q,
        (querySnapshot) => {
          const messages = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            messages.push({
              id: doc.id,
              ...data,
              created_date: data.createdDate
                ? new Date(data.createdDate)
                : new Date(),
            });
          });

          // Reverse to show oldest first in UI (since we queried desc)
          messages.reverse();

          console.log(
            `ChatService: Firebase loaded ${messages.length} messages for chatId: ${chatId}`,
          );

          // Cache the messages with pagination info
          this.messagesCache.set(chatId.toString(), {
            messages: messages,
            lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1],
            hasMore: querySnapshot.docs.length === limitCount,
            totalLoaded: messages.length,
          });

          callback({
            messages: messages,
            hasMore: querySnapshot.docs.length === limitCount,
            totalLoaded: messages.length,
            error: null,
          });
        },
        (error) => {
          console.error("ChatService: Firebase snapshot error:", error);
          const cachedData = this.getCachedMessages(chatId.toString());
          callback({
            messages: Array.isArray(cachedData)
              ? cachedData
              : cachedData.messages || [],
            hasMore: false,
            totalLoaded: 0,
            error: error.message,
          });
        },
      );

      // Store the listener for cleanup
      this.listeners.set(`messages_${chatId}`, unsubscribe);
      return unsubscribe;
    } catch (error) {
      console.error("ChatService: Error setting up Firebase listener:", error);
      callback({
        messages: [],
        hasMore: false,
        totalLoaded: 0,
        error: error.message,
      });
    }
  }

  /**
   * Send a new message using Firebase addDoc
   * @param {string|number} chatId - The chat/group ID
   * @param {Object} messageData - Message content and metadata
   * @returns {Promise<boolean>} Success status
   */
  async sendMessage(chatId, messageData) {
    if (!chatId || (!messageData?.text?.trim() && !messageData?.media)) {
      throw new Error(
        "ChatService: chatId and message text or media are required",
      );
    }

    console.log(
      "ChatService: Sending Firebase message to chatId:",
      chatId,
      messageData,
    );

    try {
      const doc = {
        chatId: String(chatId),
        messageContent: messageData.text?.trim() || "",
        messageType: messageData.media ? "media" : "sender",
        name: messageData.user_name || "Unknown User",
        userId: String(messageData.user_id),
        userImageURL: messageData.user_profile_image || "",
        media: messageData.media || "",
        createdDate: new Date().toISOString(),
        clubId: String(messageData.clubId),
        readById: [String(messageData.user_id)], // Mark as read by sender
      };
      // Optional reply-quote: {id, name, preview} of the quoted message.
      if (messageData.replyTo?.id) {
        doc.replyTo = {
          id: String(messageData.replyTo.id),
          name: messageData.replyTo.name || "User",
          preview: messageData.replyTo.preview || "",
        };
      }
      const docRef = await addDoc(collection(db, "team_chat_messages"), doc);

      console.log(
        "ChatService: Firebase message sent successfully with ID:",
        docRef.id,
      );
      return true;
    } catch (error) {
      console.error("ChatService: Error sending Firebase message:", error);
      throw new Error(`Failed to send message: ${error.message}`);
    }
  }

  /**
   * Upload a file for chat sharing
   * @param {File} file - The file to upload
   * @param {Function} onProgress - Progress callback function
   * @returns {Promise<Object>} Upload result with file information
   */
  async uploadFile(file, onProgress = null, clubId = "") {
    if (!file) {
      throw new Error("ChatService: file is required");
    }

    try {
      const formData = new FormData();
      formData.append("file", file);
      if (clubId) {
        // Backend partitions uploads into a per-club media folder.
        formData.append("clubId", clubId);
      }

      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };

      if (onProgress) {
        config.onUploadProgress = (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress(percentCompleted);
        };
      }

      const response = await HTTP.post(
        "/personal-training-BE/back-end-endpoint-chat-upload.php",
        formData,
        config,
      );

      console.log("ChatService: File uploaded successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error("ChatService: Error uploading file:", error);
      throw new Error(`Failed to upload file: ${error.message}`);
    }
  }

  /**
   * Mark message as read by adding user to readById array
   * @param {string} messageId - The message ID to mark as read
   * @param {string|number} userId - The user ID marking the message as read
   */
  async markMessageAsRead(messageId, userId) {
    if (!messageId || !userId) {
      throw new Error("ChatService: messageId and userId are required");
    }

    try {
      const messageRef = doc(db, "team_chat_messages", messageId);
      await updateDoc(messageRef, {
        readById: arrayUnion(String(userId)),
        lastReadAt: serverTimestamp(),
      });

      console.log(
        "ChatService: Message marked as read:",
        messageId,
        "by user:",
        userId,
      );
    } catch (error) {
      console.error("ChatService: Error marking message as read:", error);
      throw new Error(`Failed to mark message as read: ${error.message}`);
    }
  }

  /**
   * Update an existing message
   * @param {string} messageId - The message ID to update
   * @param {Object} updates - Fields to update
   */
  async updateMessage(messageId, updates) {
    if (!messageId || !updates) {
      throw new Error("ChatService: messageId and updates are required");
    }

    try {
      const messageRef = doc(db, "team_chat_messages", messageId);
      await updateDoc(messageRef, {
        ...updates,
        updated_date: serverTimestamp(),
      });

      console.log("ChatService: Firebase message updated:", messageId);
    } catch (error) {
      console.error("ChatService: Error updating Firebase message:", error);
      throw new Error(`Failed to update message: ${error.message}`);
    }
  }

  /**
   * Permanently delete a message from Firebase
   * @param {string} messageId - The message ID to delete
   * @returns {Promise<boolean>} Success status
   */
  async deleteMessage(messageId) {
    if (!messageId) {
      throw new Error("ChatService: messageId is required");
    }

    try {
      const messageRef = doc(db, "team_chat_messages", messageId);
      await deleteDoc(messageRef);

      console.log(
        "ChatService: Firebase message permanently deleted:",
        messageId,
      );
      return true;
    } catch (error) {
      console.error("ChatService: Error deleting Firebase message:", error);
      throw new Error(`Failed to delete message: ${error.message}`);
    }
  }

  /**
   * Delete all messages for a chat group from Firebase
   * @param {string|number} chatId - The chat/group ID
   * @param {string|number} clubId - The club ID
   * @returns {Promise<number>} Number of messages deleted
   */
  async deleteAllChatMessages(chatId, clubId) {
    if (!chatId || !clubId) {
      throw new Error("ChatService: chatId and clubId are required");
    }

    console.log(
      `ChatService: Deleting all Firebase messages for chatId: ${chatId}, clubId: ${clubId}`,
    );

    try {
      const messagesRef = collection(db, "team_chat_messages");
      const q = query(
        messagesRef,
        where("chatId", "==", String(chatId)),
        where("clubId", "==", String(clubId)),
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        console.log("ChatService: No messages to delete for this chat");
        return 0;
      }

      // Delete in batches of 500 (Firestore batch limit)
      let deletedCount = 0;
      const batchSize = 500;
      const docs = snapshot.docs;

      for (let i = 0; i < docs.length; i += batchSize) {
        const batch = writeBatch(db);
        const chunk = docs.slice(i, i + batchSize);
        chunk.forEach((docSnapshot) => {
          batch.delete(docSnapshot.ref);
        });
        await batch.commit();
        deletedCount += chunk.length;
      }

      console.log(
        `ChatService: Deleted ${deletedCount} Firebase messages for chatId: ${chatId}`,
      );
      return deletedCount;
    } catch (error) {
      console.error(
        "ChatService: Error deleting chat messages from Firebase:",
        error,
      );
      throw new Error(`Failed to delete chat messages: ${error.message}`);
    }
  }

  // ===========================================
  // 🌐 HTTP API OPERATIONS (TEAM CHAT GROUPS)
  // ===========================================

  /**
   * Get team chat groups using HTTP API
   * @param {string|number} teamId - The team ID
   * @returns {Promise<Array>} Promise that resolves to array of chat groups
   */
  async getTeamChatGroups(teamId) {
    console.log(
      "ChatService: Loading team chat groups from HTTP API for teamId:",
      teamId,
    );

    if (!teamId) {
      throw new Error("ChatService: teamId is required");
    }

    try {
      const response = await Chat.find({
        action: "getTeamChatGroupsForUser",
        teamId: parseInt(teamId),
      });

      console.log("ChatService: HTTP response for team chat groups:", response);

      if (response && response.status === 200 && response.data) {
        const groups = Array.isArray(response.data) ? response.data : [];
        console.log(
          `ChatService: Loaded ${groups.length} chat groups from HTTP API`,
        );
        return groups;
      } else {
        console.warn("ChatService: No chat groups data in HTTP API response");
        return [];
      }
    } catch (error) {
      console.error(
        "ChatService: Error loading team chat groups from HTTP API:",
        error,
      );
      throw new Error(`Failed to load team chat groups: ${error.message}`);
    }
  }

  /**
   * Save a new chat group using HTTP API
   * @param {Object} groupData - Chat group data
   * @returns {Promise<boolean>} Success status
   */
  async saveNewChatGroup(groupData) {
    if (!groupData || !groupData.name?.trim()) {
      throw new Error("ChatService: groupData with name is required");
    }

    console.log("ChatService: Saving new chat group via HTTP API:", groupData);

    try {
      const response = await Chat.find({
        action: "saveNewChatGroup",
        name: groupData.name.trim(),
        usersInTheGroup: groupData.usersInTheGroup,
        ageGroup: groupData.ageGroup,
        teamId: groupData.teamId,
        playerId: groupData.playerId,
      });

      console.log("ChatService: New chat group saved:", response);
      return response && response.status === 200;
    } catch (error) {
      console.error("ChatService: Error saving new chat group:", error);
      throw new Error(`Failed to save chat group: ${error.message}`);
    }
  }

  /**
   * Update an existing chat group using HTTP API
   * @param {Object} groupData - Chat group data with ID
   * @returns {Promise<boolean>} Success status
   */
  async updateChatGroup(groupData) {
    if (!groupData || !groupData.id) {
      throw new Error("ChatService: groupData with id is required");
    }

    console.log("ChatService: Updating chat group via HTTP API:", groupData);

    try {
      const response = await Chat.find({
        id: groupData.id,
        action: "upDateChatGroup",
        name: groupData.name,
        usersInTheGroup: groupData.usersInTheGroup,
        ageGroup: groupData.ageGroup,
        teamId: groupData.teamId,
        playerId: groupData.playerId,
      });

      console.log("ChatService: Chat group updated:", response);
      return response && response.status === 200;
    } catch (error) {
      console.error("ChatService: Error updating chat group:", error);
      throw new Error(`Failed to update chat group: ${error.message}`);
    }
  }

  /**
   * Send Firebase notification using HTTP API
   * @param {Object} notificationData - Notification data
   * @returns {Promise<boolean>} Success status
   */
  async sendNotification(notificationData) {
    console.log(
      "ChatService: Sending notification via HTTP API:",
      notificationData,
    );

    try {
      const response = await Chat.find({
        action: "sendFBNotificationMessage",
        title: notificationData.title,
        body: notificationData.body,
        link: notificationData.link,
        age_group: notificationData.age_group,
        player_id: notificationData.player_id,
        clubId: notificationData.clubId,
      });

      console.log("ChatService: Notification sent:", response);
      return response && response.status === 200;
    } catch (error) {
      console.error("ChatService: Error sending notification:", error);
      throw new Error(`Failed to send notification: ${error.message}`);
    }
  }

  // ===========================================
  // 🛠️ UTILITY METHODS
  // ===========================================

  /**
   * Clean up a specific Firebase listener
   * @param {string} listenerKey - The key of the listener to remove
   */
  removeListener(listenerKey) {
    const unsubscribe = this.listeners.get(listenerKey);
    if (unsubscribe) {
      unsubscribe();
      this.listeners.delete(listenerKey);
      console.log(`ChatService: Removed Firebase listener: ${listenerKey}`);
    }
  }

  /**
   * Clean up all active Firebase listeners
   * Call this when component is destroyed or user logs out
   */
  removeAllListeners() {
    this.listeners.forEach((unsubscribe, key) => {
      try {
        unsubscribe();
        console.log(`ChatService: Cleaned up Firebase listener: ${key}`);
      } catch (error) {
        console.error(`ChatService: Error cleaning up listener ${key}:`, error);
      }
    });

    this.listeners.clear();
    this.messagesCache.clear();
    console.log("ChatService: All Firebase listeners and caches cleared");
  }

  /**
   * Get connection status and handle offline scenarios
   * @param {Function} callback - Callback to handle connection status changes
   * @returns {Function} Unsubscribe function
   */
  getConnectionStatus(callback) {
    if (typeof callback !== "function") {
      throw new Error("ChatService: callback is required");
    }

    // Simple online/offline detection
    const handleOnline = () => callback({ isOnline: true });
    const handleOffline = () => callback({ isOnline: false });

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Initial status
    callback({ isOnline: navigator.onLine });

    // Return cleanup function
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }

  /**
   * Load more messages for pagination
   * @param {string|number} chatId - The chat/group ID
   * @param {string|number} clubId - The club ID for filtering
   * @param {number} limitCount - Number of additional messages to load (default: 20)
   * @returns {Promise<Object>} Promise with messages and pagination info
   */
  async loadMoreMessages(chatId, clubId, limitCount = 20) {
    if (!chatId || !clubId) {
      throw new Error(
        "ChatService: chatId and clubId are required for loadMoreMessages",
      );
    }

    console.log(
      `ChatService: Loading more messages for chatId: ${chatId}, limit: ${limitCount}`,
    );

    try {
      const cacheKey = chatId.toString();
      const cachedData = this.messagesCache.get(cacheKey);

      if (!cachedData || !cachedData.lastVisible) {
        console.warn("No pagination data available for loading more messages");
        return {
          messages: [],
          hasMore: false,
          totalLoaded: 0,
          error: "No pagination data available",
        };
      }

      const messagesRef = collection(db, "team_chat_messages");
      const q = query(
        messagesRef,
        where("chatId", "==", String(chatId)),
        where("clubId", "==", String(clubId)),
        orderBy("createdDate", "desc"),
        startAfter(cachedData.lastVisible),
        limit(limitCount),
      );

      const querySnapshot = await getDocs(q);
      const olderMessages = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        olderMessages.push({
          id: doc.id,
          ...data,
          created_date: data.createdDate
            ? new Date(data.createdDate)
            : new Date(),
        });
      });

      // Reverse to show oldest first in UI (since we queried desc)
      olderMessages.reverse();

      // Update cache with new pagination info
      const allMessages = [...olderMessages, ...cachedData.messages];
      const hasMore = querySnapshot.docs.length === limitCount;

      this.messagesCache.set(cacheKey, {
        messages: allMessages,
        lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1],
        hasMore: hasMore,
        totalLoaded: allMessages.length,
      });

      console.log(
        `ChatService: Loaded ${olderMessages.length} more messages, total: ${allMessages.length}`,
      );

      return {
        messages: olderMessages,
        allMessages: allMessages,
        hasMore: hasMore,
        totalLoaded: allMessages.length,
        error: null,
      };
    } catch (error) {
      console.error("ChatService: Error loading more messages:", error);
      throw new Error(`Failed to load more messages: ${error.message}`);
    }
  }

  /**
   * Get cached messages for offline viewing
   * @param {string} chatId - The chat/group ID
   * @returns {Array|Object} Cached messages or empty array
   */
  getCachedMessages(chatId) {
    const cached = this.messagesCache.get(chatId);
    return cached || [];
  }

  // ===========================================
  // 🧹 CLEANUP OPERATIONS
  // ===========================================

  /**
   * Get cleanup statistics from backend
   * @param {number} retentionDays - Number of days to retain messages
   * @returns {Promise<Object>} Statistics object
   */
  async getCleanupStats(retentionDays = 30) {
    try {
      const response = await Chat.find({
        action: "getCleanupStats",
        retentionDays: retentionDays,
      });

      if (response && response.data) {
        return response.data;
      }
      throw new Error("Failed to get cleanup statistics");
    } catch (error) {
      console.error("ChatService: Error getting cleanup stats:", error);
      throw error;
    }
  }

  /**
   * Run message cleanup
   * @param {number} retentionDays - Number of days to retain messages
   * @param {boolean} dryRun - Whether to run in dry-run mode
   * @returns {Promise<Object>} Cleanup results
   */
  async runCleanup(retentionDays = 30, dryRun = false) {
    try {
      const response = await Chat.find({
        action: "runCleanup",
        retentionDays: retentionDays,
        dryRun: dryRun,
      });

      if (response && response.data) {
        return response.data;
      }
      throw new Error("Failed to run cleanup");
    } catch (error) {
      console.error("ChatService: Error running cleanup:", error);
      throw error;
    }
  }

  /**
   * Get cleanup logs
   * @param {number} days - Number of days of logs to retrieve
   * @returns {Promise<Array>} Array of log entries
   */
  async getCleanupLogs(days = 7) {
    try {
      const response = await Chat.find({
        action: "getCleanupLogs",
        days: days,
      });

      if (response && response.data) {
        return response.data;
      }
      throw new Error("Failed to get cleanup logs");
    } catch (error) {
      console.error("ChatService: Error getting cleanup logs:", error);
      throw error;
    }
  }

  /**
   * Get cleanup configuration
   * @returns {Promise<Object>} Configuration object
   */
  async getCleanupConfig() {
    try {
      const response = await Chat.find({
        action: "getCleanupConfig",
      });

      if (response && response.data) {
        return response.data;
      }
      throw new Error("Failed to get cleanup configuration");
    } catch (error) {
      console.error("ChatService: Error getting cleanup config:", error);
      throw error;
    }
  }

  /**
   * Update cleanup configuration
   * @param {Object} config - Configuration object
   * @returns {Promise<Object>} Update result
   */
  async updateCleanupConfig(config) {
    try {
      const response = await Chat.find({
        action: "updateCleanupConfig",
        config: config,
      });

      if (response && response.data) {
        return response.data;
      }
      throw new Error("Failed to update cleanup configuration");
    } catch (error) {
      console.error("ChatService: Error updating cleanup config:", error);
      throw error;
    }
  }
}

// Export a singleton instance
export default new ChatService();

// Export the HTTP Chat utility for backward compatibility
let API_SERVER = "";
if (
  window.location.href.indexOf("8080") > -1 ||
  window.location.href.indexOf("8082") > -1
) {
  API_SERVER = "http://localhost:8888/back-end-endpoint-chat.php";
} else {
  API_SERVER = "/personal-training-BE/back-end-endpoint-chat.php";
}

export const Chat = {
  find(params) {
    return HTTP.post(API_SERVER, params);
  },
  show(id) {
    return HTTP.get(`users/${id}`);
  },
  store(post) {
    return HTTP.post(API_SERVER, post);
  },
  update(post, id) {
    return HTTP.put(`users/${id}`, post);
  },
  delete(params) {
    return HTTP.delete(API_SERVER, params);
  },
};
